/**
 * +grid: icon manifest for the Waystone Grid (docs/design/waystone-grid.md "UI"). Every star resolves
 * to an icon key: minors share one per stat, utility stars one per idle stat, ability stars one per
 * modifier kind, notables and keystones one each. Painted keys have WebP art under
 * /assets/icons/grid/<key>.webp (128 px) and <key>_64.webp (64 px), loaded only when the grid opens;
 * every key also has a clean vector glyph (24×24 SVG path data) used until the art arrives or when a
 * key has no painting (gates use their class emblem, locks their keyhole). DOM-free (unit-tested).
 */
import type { GridNode } from '@keystone/content';

/** Keys with painted art in public/assets/icons/grid (gridIcons.test.ts checks each file exists). */
export const GRID_PAINTED: ReadonlySet<string> = new Set([
  // GRID_PAINTED_BEGIN
  'dormant',
  'idle_autowits',
  'idle_gatherspeed',
  'idle_goldfind',
  'idle_offlinerate',
  'idle_oocspeed',
  'idle_profskillup',
  'keystone_blood_price',
  'keystone_glass_vow',
  'keystone_iron_root',
  'notable_afterglow',
  'notable_anvil_heart',
  'notable_bulwark_psalm',
  'notable_candlewake',
  'notable_clear_sight',
  'notable_deep_font',
  'notable_fletchers_rhythm',
  'notable_foxs_slip',
  'notable_ghost_step',
  'notable_glass_clarity',
  'notable_grindstone',
  'notable_hearth_pulse',
  'notable_hooves_of_war',
  'notable_kindred_light',
  'notable_knifes_edge',
  'notable_long_hunt',
  'notable_mercy_tide',
  'notable_pack_hunter',
  'notable_patient_blade',
  'notable_quarry',
  'notable_quickstep',
  'notable_red_tempo',
  'notable_rekindle',
  'notable_second_breath',
  'notable_spellweave',
  'notable_starlit_mind',
  'notable_static_mind',
  'notable_twin_fletch',
  'notable_unbroken_line',
  'notable_wind_at_back',
  'slot_castspeed',
  'slot_charge',
  'slot_cooldown',
  'slot_cost',
  'slot_crit',
  'slot_damage',
  'slot_extratarget',
  'slot_root',
  'stat_armor',
  'stat_cooldown',
  'stat_crit',
  'stat_damage',
  'stat_dodge',
  'stat_haste',
  'stat_healing',
  'stat_healingtaken',
  'stat_leech',
  'stat_maxhp',
  'stat_movespeed',
  'stat_regen',
  // GRID_PAINTED_END
]);

export const GRID_ICON_DIR = '/assets/icons/grid';

/** The icon key a star draws (stable across saves: derived from its effect, not its id). */
export function gridIconKey(n: GridNode): string {
  const e = n.effects[0];
  switch (n.type) {
    case 'gate':
      return `gate_${n.gate ?? 'none'}`;
    case 'lock':
      return 'lock';
    case 'dormant':
      return 'dormant';
    case 'notable':
      return e?.kind === 'notable' ? `notable_${e.id}` : 'notable';
    case 'keystone':
      return e?.kind === 'keystone' ? `keystone_${e.id}` : 'keystone';
    case 'slot':
      return e?.kind === 'slot' ? `slot_${e.mod.kind.toLowerCase()}` : 'slot';
    case 'utility':
      return e?.kind === 'idle' ? `idle_${e.stat.toLowerCase()}` : 'utility';
    default:
      return e?.kind === 'stat' ? `stat_${e.stat.toLowerCase()}` : 'minor';
  }
}

/** URL of a painted icon (null = draw the vector glyph). */
export function gridIconUrl(key: string, size: 64 | 128): string | null {
  if (!GRID_PAINTED.has(key)) return null;
  return `${GRID_ICON_DIR}/${key}${size === 64 ? '_64' : ''}.webp`;
}

// ---------------------------------------------------------------------------
// Vector fallbacks (24×24 viewBox, drawn stroked with a faint fill)
// ---------------------------------------------------------------------------

const STAR4 = 'M12 2.5l2.2 7.3 7.3 2.2-7.3 2.2L12 21.5l-2.2-7.3L2.5 12l7.3-2.2z';
const STAR8 = 'M12 1.5l2 6.2 5.4-3.1-3.1 5.4 6.2 2-6.2 2 3.1 5.4-5.4-3.1-2 6.2-2-6.2-5.4 3.1 3.1-5.4-6.2-2 6.2-2-3.1-5.4 5.4 3.1z';

const GLYPH: Readonly<Record<string, string>> = {
  stat_maxhp: 'M12 20.5S4 15.2 4 9.4A4.3 4.3 0 0 1 12 7a4.3 4.3 0 0 1 8 2.4c0 5.8-8 11.1-8 11.1z',
  stat_damage: 'M12 2.5l2 3v10.5h-4V5.5zM7.5 16h9M12 16v5.5M10.5 21.5h3',
  stat_healing: 'M5 19c0-8 5-13 14-14-1 9-6 14-14 14zM5 19l8-8',
  stat_haste: 'M7 3h10M7 21h10M8 3c0 5 8 6 8 9s-8 4-8 9M16 3c0 5-8 6-8 9s8 4 8 9',
  stat_crit: 'M12 2.5 15 12l-3 9.5L9 12zM3 12h4M17 12h4',
  stat_armor: 'M6 4h12l1 7c0 5-3 8-7 9.5C8 19 5 16 5 11zM12 4v16.5M6 10h12',
  stat_dodge: 'M4 12c3-6 13-6 16 0M4 12c3 6 13 6 16 0M3 4l18 16',
  stat_regen: 'M12 3s-6 7-6 11a6 6 0 0 0 12 0c0-4-6-11-6-11zM9.5 15a2.5 2.5 0 0 0 2.5 2.5',
  stat_cooldown: 'M12 4a8 8 0 1 1-7.4 5M4.5 4v5h5M12 8v4l3 2',
  stat_leech: 'M12 3s-6 7-6 11a6 6 0 0 0 12 0c0-4-6-11-6-11zM9 14h6',
  stat_healingtaken: 'M4 15h4l3 3h6a2 2 0 0 0 0-4h-4M12 3v7M8.5 6.5h7',
  stat_movespeed: 'M4 17h9l3-5 4 2-3 5H4zM8 12l2-6M12 12l1-5M3 9h4M2 13h4',
  idle_gatherspeed: 'M6 20c0-6 2-11 8-14M14 6c3 0 5 2 5 5-3 0-5-2-5-5zM9 12c-3 0-4-2-4-4 3 0 4 2 4 4z',
  idle_goldfind: 'M12 3a9 9 0 1 0 .1 0zM12 7v10M15 9c-1-1-5-1.5-5 1s5 1.5 5 4-4 2-5 1',
  idle_autowits: 'M5 9a7 7 0 0 1 14 0v5a7 7 0 0 1-14 0zM9 10.5h.1M15 10.5h.1M10 15h4M5 6 3 3M19 6l2-3',
  idle_oocspeed: 'M4 20c4-1 5-5 8-8s6-4 8-8M7 4v16M5 8h4',
  idle_profskillup: 'M4 20l8-8M10 8l4-4 4 4-4 4zM15 14h5v6h-5z',
  idle_offlinerate: 'M16 4a8 8 0 1 0 4 12 6.5 6.5 0 0 1-4-12z',
  slot_damage: 'M4 20 16 8M14 4l6 6-2 2-6-6zM6 14l4 4',
  slot_cooldown: 'M12 4a8 8 0 1 1-7.4 5M4.5 4v5h5',
  slot_cost: 'M12 3 6 11l6 10 6-10zM6 11h12M4 20 20 4',
  slot_crit: 'M12 3a9 9 0 1 0 .1 0zM12 8a4 4 0 1 0 .1 0zM12 12h.1M3 3l6 6',
  slot_castspeed: 'M4 18c4-1 8-5 10-10M9 19c4-1 7-4 9-8M14 4l1.5 3 3 1.5-3 1.5L14 13l-1.5-3-3-1.5 3-1.5z',
  slot_charge: 'M5 6h6v12H5zM13 6h6v12h-6z',
  slot_extratarget: 'M3 12h7M10 12l9-7M10 12h11M10 12l9 7',
  slot_root: 'M12 21v-8M12 13c-5 0-7-3-7-7 4 0 7 2 7 7zM12 13c5 0 7-3 7-7-4 0-7 2-7 7zM8 21h8',
  notable: STAR4,
  keystone: STAR8,
  dormant: 'M12 4a8 8 0 1 0 .1 0zM12 9v.1M12 12v3',
  lock: 'M12 6a3 3 0 0 1 1.6 5.5L15 18H9l1.4-6.5A3 3 0 0 1 12 6z',
  minor: 'M12 7a5 5 0 1 0 .1 0z',
  slot: 'M12 3l8 9-8 9-8-9z',
  utility: 'M12 3l7.8 4.5v9L12 21l-7.8-4.5v-9z',
};

/** The vector glyph for a key (notables, keystones and unknown keys fall back by kind). */
export function gridGlyph(key: string): string {
  if (GLYPH[key]) return GLYPH[key]!;
  const kind = key.split('_')[0]!;
  if (kind === 'notable') return GLYPH.notable!;
  if (kind === 'keystone') return GLYPH.keystone!;
  if (kind === 'stat') return GLYPH.minor!;
  if (kind === 'idle') return GLYPH.utility!;
  if (kind === 'slot') return GLYPH.slot!;
  return GLYPH.minor!;
}

/** Short badge text for an ability star's selector ("2", "AoE", …), drawn on the frame. */
export function slotBadge(n: GridNode): string {
  const e = n.effects[0];
  if (e?.kind !== 'slot') return '';
  if ('key' in e.sel) return e.sel.key;
  const t: Record<string, string> = { aoe: 'A', defensive: 'D', spender: 'S', builder: 'B', movement: 'M', interrupt: 'I', heal: 'H' };
  return t[e.sel.tag] ?? '';
}
